"use client";
import { type ReactNode, createContext, useRef, useContext } from "react";
import { useStore } from "zustand";

import {
	CourseSearchStore,
	createEDocumentFilterStore,
} from "./edoc-filter-store";
import { EDocumentQueryParams } from "@onair/services/e-document";

export type EDocFilterStoreApi = ReturnType<typeof createEDocumentFilterStore>;

export const EDocFilterContext = createContext<EDocFilterStoreApi | undefined>(
	undefined,
);

export interface EDocStoreProviderProps {
	children: ReactNode;
	defaultQueryParams?: EDocumentQueryParams;
}

export const EDocFilterProvider = ({
	children,
	defaultQueryParams,
}: EDocStoreProviderProps) => {
	const storeRef = useRef<EDocFilterStoreApi | null>(null);
	if (storeRef.current === null) {
		storeRef.current = createEDocumentFilterStore({
			q: defaultQueryParams?.filter,
			limit: defaultQueryParams?.limit,
			offset: defaultQueryParams?.offset,
			order: defaultQueryParams?.order,
			orderField: defaultQueryParams?.orderField,
		});
	}

	return (
		<EDocFilterContext.Provider value={storeRef.current}>
			{children}
		</EDocFilterContext.Provider>
	);
};

export const useCourseFilter = <T,>(
	selector: (store: CourseSearchStore) => T,
): T => {
	const context = useContext(EDocFilterContext);

	if (!context) {
		throw new Error(
			`useCourseSearchStore must be used within EDocFilterProvider`,
		);
	}

	return useStore(context, selector);
};
